/**
 * LocalStorage cache for user profile data.
 * Used by navigation components to display user info without API calls.
 */

const PROFILE_CACHE_KEY = "local-links-user-profile";

export interface CachedUserProfile {
  id: string;
  displayName: string | null;
  photoURL: string | null;
  email: string | null;
  handicap: number | string | null;
  homeCourseName: string | null;
  subscriptionTier: "free" | "member" | null;
  location: {
    zip?: string;
    latitude?: number;
    longitude?: number;
  } | null;
  updatedAt: number; // timestamp
}

/**
 * Save user profile to localStorage
 */
export function saveProfileToCache(
  profile: Omit<CachedUserProfile, "updatedAt">
): void {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(
      PROFILE_CACHE_KEY,
      JSON.stringify({
        ...profile,
        updatedAt: Date.now(),
      })
    );
  } catch (error) {
    console.warn("Failed to save profile to localStorage:", error);
  }
}

/**
 * Get user profile from localStorage
 */
export function getProfileFromCache(): CachedUserProfile | null {
  if (typeof window === "undefined") return null;

  try {
    const cached = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!cached) return null;

    return JSON.parse(cached) as CachedUserProfile;
  } catch (error) {
    console.warn("Failed to read profile from localStorage:", error);
    return null;
  }
}

/**
 * Clear user profile from localStorage (on logout)
 */
export function clearProfileCache(): void {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem(PROFILE_CACHE_KEY);
  } catch (error) {
    console.warn("Failed to clear profile from localStorage:", error);
  }
}

/**
 * Update specific fields in the cached profile
 */
export function updateProfileCache(
  updates: Partial<Omit<CachedUserProfile, "updatedAt">>
): void {
  const current = getProfileFromCache();
  if (!current) return;

  saveProfileToCache({
    ...current,
    ...updates,
  });
}
